import React, { useState, useRef, useEffect } from "react";
import { useChat } from "../context/ChatContext";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Card } from "./ui/Card";
import { Stack } from "./ui/Stack";
import { Text } from "./ui/Text";

export const ChatWidget: React.FC = () => {
 const {
  isOpen,
  messages,
  loading,
  unreadCount,
  openChat,
  closeChat,
  sendMessage,
 } = useChat();
 const [message, setMessage] = useState("");
 const messagesEndRef = useRef<HTMLDivElement>(null);

 useEffect(() => {
  if (isOpen) {
   messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }
 }, [messages, isOpen]);

 const handleSend = async (e: React.FormEvent) => {
  e.preventDefault();
  if (message.trim()) {
   const messageText = message.trim();
   setMessage(""); // Clear input immediately for better UX
   await sendMessage(messageText);
  }
 };

 if (!isOpen) {
  return (
   <div className="fixed bottom-4 right-4 z-50 md:bottom-24">
    <Button
     onClick={openChat}
     className=" w-14 h-14 relative"
    >
     <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
     >
      <path
       strokeLinecap="round"
       strokeLinejoin="round"
       strokeWidth={2}
       d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
     </svg>
     {unreadCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center">
       {unreadCount > 9 ? "9+" : unreadCount}
      </span>
     )}
    </Button>
   </div>
  );
 }

 return (
  <div className="fixed bottom-4 right-4 z-50 w-96 max-w-full md:bottom-24 md:right-4 sm:right-2">
   <Card padding="none" className="">
    <div className="flex flex-col h-[500px]">
     {/* Header */}
     <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
      <Text weight="semibold" className="text-white">
       Customer Support
      </Text>
      <Button
       variant="ghost"
       size="sm"
       onClick={closeChat}
       className="text-white hover:bg-blue-700"
      >
       <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
       </svg>
      </Button>
     </div>

     {/* Messages */}
     <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
      {loading && messages.length === 0 ? (
       <div className="text-center text-gray-500">Loading messages...</div>
      ) : messages.length === 0 ? (
       <div className="text-center text-gray-500">
        Start a conversation with our support team!
       </div>
      ) : (
       messages.map((msg) => {
        const isCustomer = msg.senderType === "customer";
        return (
         <div
          key={msg._id}
          className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}
         >
          <div
           className={`max-w-[80%] p-3 ${
            isCustomer
             ? "bg-blue-600 text-white"
             : "bg-white text-gray-900 border border-gray-200"
           }`}
          >
           {!isCustomer && (
            <div className="text-xs font-semibold mb-1 text-gray-600">
             {msg.sender.name}
            </div>
           )}
           <Text className={isCustomer ? "text-white" : ""}>{msg.message}</Text>
           <div
            className={`text-xs mt-1 ${
             isCustomer ? "text-blue-100" : "text-gray-500"
            }`}
           >
            {new Date(msg.createdAt).toLocaleTimeString([], {
             hour: "2-digit",
             minute: "2-digit",
            })}
           </div>
          </div>
         </div>
        );
       })
      )}
      <div ref={messagesEndRef} />
     </div>

     {/* Input */}
     <form onSubmit={handleSend} className="p-4 border-t border-gray-200 bg-white">
      <Stack direction="row" spacing="sm">
       <Input
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Type your message..."
        className="flex-1"
       />
       <Button type="submit" disabled={!message.trim() || loading}>
        Send
       </Button>
      </Stack>
     </form>
    </div>
   </Card>
  </div>
 );
};

